import React, { useEffect, useState } from "react";
import styles from "./time-in-shipment-flow.module.css";
import { AdminPartialBatch } from "@boxem/core-api-schema";
import { getDifferenceInMinutes } from "@/utils/dateUtils";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import Flex from "@/components/Flex/Flex";

type ChartData = {
  tt: string; //TransportType
  AvgTime: number;
};

type TimeInShimentFlowProps = {
  batches: AdminPartialBatch[];
  loading: boolean;
};

const TimeInShimentFlow = ({
  batches,
  loading,
}: TimeInShimentFlowProps) => {
  const [chartData, setChartData] = useState<ChartData[]>([]);

  const generateChartData = () => {
    //For step 1 -> batch.created_at to INBOUND_PACKING_OPTION_FETCHED status.
    //For step 2 -> INBOUND_PACKING_OPTION_FETCHED to INBOUND_SHIPMENT_OPTION_LOADED.
    //For step 3 -> INBOUND_SHIPMENT_OPTION_LOADED to INBOUND_TRANSPORT_OPTION_CONFIRMED
    //For step 4 -> INBOUND_TRANSPORT_OPTION_CONFIRMED to when all shipments in batch were patched into a "IN_TRANSIT" or "CANCELLED" state.
    let cd = [
      {
        tt: "SPD",
        AvgTime: 0,
      },
      {
        tt: "LTL",
        AvgTime: 0,
      },
    ];
    let totalValidBatches = 0;

    batches.map(batch => {
      const {
        step1EndDate,
        step2EndDate,
        step3EndDate,
        shipmentType,
        createdAt,
      } = batch;

      //Don't use batch in calculation if any of the timestamps are an empty string.
      if (step1EndDate === "" || step2EndDate === "" || step3EndDate === "") {
        return;
      }

      totalValidBatches++;

      const createdAndFinishedDiff = (!!createdAt && !!step3EndDate) ? getDifferenceInMinutes(createdAt, step3EndDate) : 0;

      if (shipmentType === "SPD") {
        if (!isNaN(createdAndFinishedDiff)) cd[0].AvgTime += createdAndFinishedDiff;
      } else if (shipmentType === "LTL") {
        if (!isNaN(createdAndFinishedDiff)) cd[1].AvgTime += createdAndFinishedDiff;
      }
    });

    cd = cd.map(c => {
      c.AvgTime = !!c.AvgTime ? c.AvgTime / totalValidBatches : 0;
      return c;
    })

    setChartData(cd);
  };

  useEffect(() => {
    if (!loading && !!batches) generateChartData();
  }, [batches, loading]);

  return (
    <Flex
      justify="center"
      align="center"
      direction="column"
      className={styles.chartContainer}
    >
      {(loading) ? <LoadingSpinner size="3rem" /> : (
        <>
          <h3 className={`${styles.chartHeader} strong`}>Avg Time Spent In Shipment Flow In Minutes</h3>
          <ResponsiveContainer
            height="100%"
            width="100%"
          >
            <BarChart
              width={450}
              height={500}
              data={chartData}
              margin={{
                top: 5,
                right: 5,
                left: 5,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="tt" stroke="var(--static-text-strong)" />
              <YAxis stroke="var(--static-text-strong)" />
              <Tooltip
                wrapperStyle={{
                  backgroundColor: "var(--static-background-strong)",
                }}
              />
              <Legend fill="var(--static-text-strong)" />
              <Bar dataKey="AvgTime" fill="var(--color-primary)" />
            </BarChart>
          </ResponsiveContainer>
        </>
      )}
    </Flex>
  );
};

export default TimeInShimentFlow;